import { useState } from 'react';
import { Link } from 'react-router-dom';

export function ResourcesPage() {
  const [email, setEmail] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSubmitted(true);
    setIsSubmitting(false);
  };

  return (
    <div className="bg-off-white">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-olive-900 mb-6 leading-tight">
            How iOS Is Quietly Draining $10K+/Month From Your Shopify Store
          </h1>
          <p className="text-lg md:text-xl text-olive-700 mb-4 font-medium">
            (And the Server-Side Fix Most Merchants Don't Know About)
          </p>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A no-BS breakdown of what Apple actually broke, why your Facebook numbers are lying, and exactly what to do about it before 2026 makes it worse.
          </p>
          <a
            href="#get-guide"
            className="btn-primary inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white"
          >
            Send Me the Free Guide →
          </a>
        </div>
      </section>

      {/* Opening - The Hook */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-lg">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              <strong className="text-olive-900">You've noticed it.</strong>
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Your ad spend goes up. Your ROAS goes down. Facebook says you made money last month, but when you check your bank account, the numbers don't match.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              <strong className="text-olive-800">You're not imagining it. And it's not your fault.</strong>
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Since iOS 14.5, Apple has been systematically blocking Meta from seeing your customers. Every time someone with an iPhone opts out of tracking—which is now <span className="text-olive-700 font-semibold">over 85% of users</span>—your pixel goes blind.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              The result?
            </p>
            
            <div className="glass-olive rounded-xl p-8 mb-8">
              <p className="text-2xl md:text-3xl font-bold gradient-text-olive text-center">
                30-60% of your conversions never reach Facebook.
              </p>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              You're not just losing data. You're losing the ability to make intelligent decisions about where to put your money.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              And here's the part that really stings: you've probably already <strong className="text-olive-800">killed a winning campaign</strong> because Facebook told you it wasn't working. Or <strong className="text-olive-800">scaled a loser</strong> because the algorithm couldn't see who was actually buying.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              This guide breaks down exactly what's happening, why it's getting worse, and what the server-side fix looks like—without the $500/month price tag or the 47 dashboards you'll never use.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="divider-olive max-w-2xl mx-auto"></div>

      {/* Section 1: What iOS 14 Actually Broke */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-section">
        <div className="max-w-3xl mx-auto">
          <span className="badge-olive inline-block px-3 py-1 rounded-full text-sm font-medium mb-4">
            Section 1
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-olive-900 mb-3">
            What iOS 14 Actually Broke
          </h2>
          <p className="text-lg text-olive-700 font-medium mb-8">
            It's Not a Bug. It's a Feature. (Apple's Feature. Your Problem.)
          </p>
          
          <div className="prose prose-lg">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              In April 2021, Apple released iOS 14.5 with a feature called <strong className="text-olive-800">App Tracking Transparency (ATT)</strong>. It sounds innocent. It's not.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              <strong className="text-olive-900">Here's what ATT does:</strong>
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              When someone opens Facebook or Instagram on an iPhone, they see a popup: <em>"Allow this app to track your activity across other companies' apps and websites?"</em>
            </p>
            
            <div className="glass-card rounded-xl p-6 mb-8">
              <p className="text-xl font-bold text-olive-800 text-center">
                Over 85% of users tap "Ask App Not to Track."
              </p>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              The moment they do, Facebook loses the ability to:
            </p>
            
            <ul className="space-y-3 mb-8">
              {[
                "See when that user visits your website",
                "Track when they add something to cart",
                "Register when they complete a purchase",
                "Connect that purchase back to the ad that drove it"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <span className="text-olive-600 mt-1 flex-shrink-0">✗</span>
                  <span className="text-lg">{item}</span>
                </li>
              ))}
            </ul>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Your Meta pixel still fires. But the data goes nowhere. <strong className="text-olive-800">Facebook never receives it.</strong>
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              So when you look at your Ads Manager and see "47 purchases attributed," the real number might be 80. Or 120. You'll never know—because Facebook literally cannot see the majority of your conversions.
            </p>
            
            <h3 className="text-xl font-bold text-olive-900 mb-4">
              The Compounding Problem: AdBlockers
            </h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              iOS isn't the only culprit.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Browser-based AdBlockers (used by <strong className="text-olive-700">30-40% of desktop users</strong>) intercept your pixel script before it can execute. The pixel doesn't just fail to send data—it never fires in the first place.
            </p>
            
            <div className="section-gradient-box rounded-xl p-8 mb-6">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Between iOS opt-outs and AdBlockers, you're operating with somewhere between <span className="font-bold text-olive-800">40-70% of your actual conversion data</span>.
              </p>
              <p className="text-lg font-semibold text-olive-900">
                And you're making five-figure monthly decisions based on it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Why Your Current Setup Can't Fix This */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <span className="badge-olive inline-block px-3 py-1 rounded-full text-sm font-medium mb-4">
            Section 2
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-olive-900 mb-3">
            Why Your Current Setup Can't Fix This
          </h2>
          <p className="text-lg text-olive-700 font-medium mb-8">
            Client-Side Tracking Is Broken by Design
          </p>
          
          <div className="prose prose-lg">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Here's the technical reality in plain English:
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Your Meta pixel is <strong className="text-olive-800">"client-side."</strong> That means the tracking code runs in the user's browser. When someone visits your site, their browser executes the pixel script and sends data to Facebook.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The problem? <strong className="text-olive-800">iOS and AdBlockers operate at the browser level.</strong> They intercept the signal before it leaves.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              It doesn't matter how well your pixel is configured. It doesn't matter if you've set up every event perfectly. If the user's device blocks the transmission, Facebook never sees it.
            </p>
            
            <div className="glass-olive rounded-xl p-8">
              <p className="text-lg text-olive-900 font-semibold mb-2">
                This is why your pixel setup isn't the problem.
              </p>
              <p className="text-xl font-bold text-olive-800">
                The architecture is the problem.
              </p>
              <p className="text-base text-gray-700 mt-4">
                No amount of pixel optimization fixes a system that's fundamentally broken at the transmission layer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="divider-olive max-w-2xl mx-auto"></div>

      {/* Section 3: The Server-Side Solution */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-section">
        <div className="max-w-3xl mx-auto">
          <span className="badge-olive inline-block px-3 py-1 rounded-full text-sm font-medium mb-4">
            Section 3
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-olive-900 mb-3">
            The Server-Side Solution
          </h2>
          <p className="text-lg text-olive-700 font-medium mb-8">
            What Meta's Conversions API Actually Does (And Why Most Merchants Implement It Wrong)
          </p>
          
          <div className="prose prose-lg">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Meta knew client-side tracking was dying. So they built the <strong className="text-olive-800">Conversions API (CAPI)</strong>.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              <strong className="text-olive-900">Here's how it works:</strong>
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Instead of your customer's browser sending data to Facebook, <strong className="text-olive-800">your server sends the data directly</strong>.
            </p>
            
            {/* Visual flow */}
            <div className="glass-card rounded-xl p-6 mb-8">
              <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
                <div className="bg-olive-100 px-4 py-2 rounded-lg text-olive-800 font-medium">
                  User visits your site
                </div>
                <span className="text-olive-400 text-xl hidden md:block">→</span>
                <span className="text-olive-400 text-xl md:hidden">↓</span>
                <div className="bg-olive-100 px-4 py-2 rounded-lg text-olive-800 font-medium">
                  Your server captures the event
                </div>
                <span className="text-olive-400 text-xl hidden md:block">→</span>
                <span className="text-olive-400 text-xl md:hidden">↓</span>
                <div className="bg-olive-600 px-4 py-2 rounded-lg text-white font-medium">
                  Server sends it to Meta
                </div>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              <strong className="text-olive-800">iOS can't block it. AdBlockers can't intercept it.</strong> The data travels server-to-server, completely bypassing the browser.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Sounds perfect. So why isn't everyone using it?
            </p>
            
            <p className="text-xl font-bold text-olive-900 mb-6">
              Because implementation is a nightmare.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Meta's documentation assumes you have:
            </p>
            
            <ul className="space-y-3 mb-8">
              {[
                "A developer who understands API authentication",
                "The ability to modify your server infrastructure",
                "Time to manually configure every event type",
                "Ongoing resources to maintain and debug"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <span className="text-olive-600 mt-1 flex-shrink-0">•</span>
                  <span className="text-lg">{item}</span>
                </li>
              ))}
            </ul>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              For a solo founder or small team running a Shopify store? <strong className="text-olive-800">That's not realistic.</strong>
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              So merchants either:
            </p>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-3 text-gray-700">
                <span className="text-olive-600 mt-1 flex-shrink-0">1.</span>
                <span className="text-lg">Ignore CAPI entirely (and keep losing data)</span>
              </li>
              <li className="flex items-start gap-3 text-gray-700">
                <span className="text-olive-600 mt-1 flex-shrink-0">2.</span>
                <span className="text-lg">Pay $300-500/month for tools like Hyros, Elevar, or Triple Whale</span>
              </li>
              <li className="flex items-start gap-3 text-gray-700">
                <span className="text-olive-600 mt-1 flex-shrink-0">3.</span>
                <span className="text-lg">Hire a developer for a custom implementation</span>
              </li>
            </ul>
            
            <p className="text-lg font-semibold text-olive-900">
              None of those options make sense for a brand spending $5K-$50K/month on ads.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4: The Tracking Landscape in 2026 */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <span className="badge-olive inline-block px-3 py-1 rounded-full text-sm font-medium mb-4">
            Section 4
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-olive-900 mb-3">
            The Tracking Landscape in 2026
          </h2>
          <p className="text-lg text-olive-700 font-medium mb-8">
            It's Getting Worse. Here's What's Coming.
          </p>
          
          <div className="prose prose-lg">
            <p className="text-xl font-semibold text-olive-900 mb-8">
              iOS 14 was just the beginning.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-bold text-olive-900 mb-4">
                  What's already happened:
                </h3>
                <ul className="space-y-3">
                  {[
                    "iOS 15 introduced Mail Privacy Protection (killing email open tracking)",
                    "iOS 17 added Link Tracking Protection (stripping UTM parameters)",
                    "Safari now blocks all third-party cookies by default",
                    "Chrome is phasing out third-party cookies (delayed but coming)"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <span className="text-olive-600 mt-0.5 flex-shrink-0">✓</span>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="section-gradient-box rounded-xl p-6">
                <h3 className="text-lg font-bold text-olive-900 mb-4">
                  What's coming:
                </h3>
                <ul className="space-y-3">
                  {[
                    "Further restrictions on cross-site tracking",
                    "More aggressive browser-level blocking",
                    "Potential EU regulations requiring explicit consent for any tracking",
                    "Meta's own limitations on data retention and usage"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <span className="text-olive-600 mt-0.5 flex-shrink-0">→</span>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="glass-olive rounded-xl p-8">
              <p className="text-lg font-semibold text-olive-900 text-center">
                The merchants who survive this shift are the ones who move to server-side tracking <span className="text-olive-700">now</span>—before the next wave of restrictions makes client-side completely useless.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="divider-olive max-w-2xl mx-auto"></div>

      {/* Section 5: What "Revenue Recovered" Actually Means */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-section">
        <div className="max-w-3xl mx-auto">
          <span className="badge-olive inline-block px-3 py-1 rounded-full text-sm font-medium mb-4">
            Section 5
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-olive-900 mb-3">
            What "Revenue Recovered" Actually Means
          </h2>
          <p className="text-lg text-olive-700 font-medium mb-8">
            The Metric That Tells You If It's Working
          </p>
          
          <div className="prose prose-lg">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Here's how you calculate what you're losing:
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="glass-card rounded-lg p-4 flex items-center gap-4">
                <span className="bg-olive-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</span>
                <p className="text-gray-700">Look at your Facebook-reported purchases for last month.</p>
              </div>
              <div className="glass-card rounded-lg p-4 flex items-center gap-4">
                <span className="bg-olive-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</span>
                <p className="text-gray-700">Look at your actual Shopify orders that came from paid traffic.</p>
              </div>
              <div className="glass-card rounded-lg p-4 flex items-center gap-4">
                <span className="bg-olive-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</span>
                <p className="text-gray-700">The gap is your <strong className="text-olive-800">lost attribution</strong>.</p>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              For most merchants, that gap is <strong className="text-olive-800">30-60% of total conversions</strong>.
            </p>
            
            <div className="section-gradient-box rounded-xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                If Facebook says you had 100 purchases from ads, you probably had <strong className="text-olive-800">140-160</strong>. If your AOV is $75, that's <strong className="text-olive-800">$3,000-$4,500</strong> in revenue Facebook doesn't know about.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Now multiply that by 12 months.
              </p>
              <p className="text-xl font-bold text-olive-900">
                That's $36,000-$54,000 in annual revenue that's invisible to your ad optimization.
              </p>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Your campaigns are underperforming on paper even when they're actually profitable.
            </p>
            
            <div className="glass-olive rounded-xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong className="text-olive-900">"Revenue Recovered"</strong> is the dollar amount of conversions that server-side tracking captured and sent to Meta—conversions that would have been lost without it.
              </p>
              <p className="text-lg font-semibold text-olive-900">
                When that number exceeds what you pay for tracking, you're profitable. Simple.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: What To Do About It */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <span className="badge-olive inline-block px-3 py-1 rounded-full text-sm font-medium mb-4">
            Section 6
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-olive-900 mb-3">
            What To Do About It
          </h2>
          <p className="text-lg text-olive-700 font-medium mb-8">
            Your Options (Ranked by Sanity)
          </p>
          
          <div className="space-y-6 mb-8">
            {/* Option 1 */}
            <div className="glass-card rounded-xl p-6 hover-lift">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-bold text-gray-500">Option 1: Do Nothing</h3>
                <span className="text-sm text-gray-500 font-medium">Not Recommended</span>
              </div>
              <p className="text-gray-600 mb-3">
                Keep losing 30-60% of your data. Keep making blind decisions. Hope it works out.
              </p>
              <p className="text-sm font-medium text-gray-500">
                Cost: Free (but expensive)
              </p>
            </div>
            
            {/* Option 2 */}
            <div className="glass-card rounded-xl p-6 hover-lift">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-bold text-gray-700">Option 2: Hire a Developer</h3>
              </div>
              <p className="text-gray-600 mb-3">
                Custom CAPI implementation. Requires ongoing maintenance. Breaks when Shopify updates.
              </p>
              <p className="text-sm font-medium text-gray-500">
                Cost: $2,000-$10,000 upfront + ongoing
              </p>
            </div>
            
            {/* Option 3 */}
            <div className="glass-card rounded-xl p-6 hover-lift">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-bold text-gray-700">Option 3: Enterprise Tools (Hyros, Elevar, Triple Whale)</h3>
              </div>
              <p className="text-gray-600 mb-3">
                Full attribution platforms. 47 dashboards. Requires onboarding calls to understand.
              </p>
              <p className="text-sm font-medium text-gray-500">
                Cost: $300-$500/month
              </p>
            </div>
            
            {/* Option 4 - Highlighted */}
            <div className="glass-olive rounded-xl p-6 outline-medium hover-lift">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-bold text-olive-900">Option 4: Sareto</h3>
                <span className="badge-olive text-sm px-2 py-1 rounded-full font-medium">Recommended</span>
              </div>
              <p className="text-olive-800 mb-3">
                One-click server-side tracking. Shopify-native. Essential metrics. No complexity.
              </p>
              <p className="text-lg font-bold text-olive-700">
                Cost: $49/month
              </p>
            </div>
          </div>
          
          <p className="text-lg text-center text-gray-700 font-medium">
            We built Sareto because we couldn't find Option 4 anywhere else.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-guide" className="py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-hero">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-olive-900 mb-6">
            Get the Full Guide + Early Access
          </h2>
          <p className="text-lg text-gray-700 mb-4">
            This page covers the highlights. The full guide goes deeper:
          </p>
          
          <ul className="text-left max-w-md mx-auto space-y-3 mb-10">
            {[
              "Technical diagrams of client-side vs. server-side data flow",
              "Step-by-step calculation of your current data loss",
              "Platform-by-platform breakdown of tracking restrictions",
              "2026 preparation checklist"
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3 text-gray-700">
                <span className="text-olive-600 mt-1 flex-shrink-0">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          
          <p className="text-gray-700 mb-8">
            Enter your email below. We'll send you the guide immediately, plus early access to Sareto when we launch.
          </p>
          
          {/* Form */}
          <div className="glass-strong rounded-2xl p-8 max-w-md mx-auto">
            {submitted ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-olive-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-olive-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-olive-900 mb-2">Check your inbox!</h3>
                <p className="text-gray-600">
                  The guide is on its way. We'll also let you know when Sareto is ready.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email Address"
                    required
                    className="input-glass w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
                  />
                </div>
                <div>
                  <input
                    type="url"
                    value={storeUrl}
                    onChange={(e) => setStoreUrl(e.target.value)}
                    placeholder="Store URL (optional)"
                    className="input-glass w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full px-6 py-4 rounded-lg font-semibold text-white disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Send Me the Guide →'
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  No spam. Just the guide and occasional updates. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
          
          <div className="mt-10">
            <Link
              to="/waitlist"
              className="text-olive-600 hover:text-olive-700 font-medium transition-colors"
            >
              Or join the waitlist directly →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
